/* Panneau de chat Naïa partagé — content-editor.tsx et la page de
   structuration post-import (spec 11). Purement présentationnel : l'état et
   la logique d'envoi vivent dans le hook useDocumentChat. */

import { type FormEvent, type KeyboardEvent } from 'react';
import { ArrowCounterClockwise, ArrowRight, Paperclip, WarningCircle, X } from '@phosphor-icons/react';
import Markdown from './markdown';
import type { TeacherMsg } from '../lib/editor-chat';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

/* Les deux moities repondent a deux chemins differents, mesures separement :
   `min-w-0` leve le plancher min-content de la colonne flex (sans lui, un
   message d'assistant de 5 000 caracteres insecables la portait a 37 561 px) ;
   `overflow-wrap:anywhere` coupe le message de l'ENSEIGNANT, rendu en texte nu
   que le `break-word` d'index.css (p/h1..h6) n'atteint jamais.
   Preuve au rendu : devtools/ui-audit/confinement-bulle.mjs. */
const BUBBLE_CLASS =
  "rounded-card p-[10px_14px] text-[13px] leading-[1.55] text-foreground bg-naia-card-alt min-w-0 [overflow-wrap:anywhere]";

export function DocumentSidechat({
  messages, streaming, streamText, chatError, chatErrorIsPartialWrite, onRetry, draft, setDraft, handleKey, send,
  scrollRef, pendingImport, onClearPendingImport, exhaustedNotice, truncatedNotice, onContinue, isExam,
  placeholder = 'Demandez à Naïa… (Entrée pour envoyer)',
  emptyHint = 'Naïa peut lire, créer et modifier les sections de ce document. Décrivez ce que vous voulez.',
  activeBadge,
  disabledReason,
}: {
  messages: TeacherMsg[]; streaming: boolean; streamText: string; chatError: string | null;
  chatErrorIsPartialWrite: boolean; onRetry: () => Promise<void>;
  exhaustedNotice: string | null; truncatedNotice: boolean; onContinue: () => void;
  draft: string; setDraft: (v: string) => void; handleKey: (e: KeyboardEvent<HTMLTextAreaElement>) => void; send: (e: FormEvent) => void; scrollRef: React.RefObject<HTMLDivElement | null>;
  pendingImport: { id: string; mime_type: string; variant: 'subject' | 'answer_key' } | null; onClearPendingImport: () => void;
  isExam: boolean;
  placeholder?: string;
  emptyHint?: string;
  /** Lot 3 (spec 13, exam-import-review) — le chat édite la variante affichée
   *  à l'écran ; ce badge indique laquelle, mis à jour à chaque changement
   *  d'onglet. */
  activeBadge?: string | null;
  /** Lot 2 (spec 13, content-editor classique) — toujours en vigueur sur cette
   *  page : désactive le composeur tant qu'une variante non-référence est
   *  affichée. */
  disabledReason?: string | null;
}) {
  const chatDisabled = Boolean(disabledReason);
  return (
    <>
      <div ref={scrollRef} className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden p-[16px_16px_8px]">
        {activeBadge && (
          <div className="mb-3 inline-flex items-center gap-1 rounded-full bg-naia-info-bg px-3 py-1 text-[11.5px] font-semibold text-naia-blue">
            {activeBadge}
          </div>
        )}
        {messages.length === 0 && !streaming && (
          <p className="text-muted-foreground text-[13px] m-0">{emptyHint}</p>
        )}
        {messages.map((m, i) => (
          <div key={i} className="mb-3">
            <div className="text-[11px] font-bold text-muted-foreground mb-[3px]">{m.role === 'user' ? 'Vous' : 'Naïa'}</div>
            <div className={cn(BUBBLE_CLASS, m.role === 'user' && "bg-naia-info-bg")}>
              {m.role === 'assistant' ? <Markdown text={m.content} /> : m.content}
            </div>
          </div>
        ))}
        {streaming && streamText && (
          <div className="mb-3">
            <div className="text-[11px] font-bold text-muted-foreground mb-[3px]">Naïa</div>
            <div className={BUBBLE_CLASS}><Markdown text={streamText} /></div>
          </div>
        )}
        {streaming && !streamText && <div className="text-muted-foreground text-[12px] italic">Naïa réfléchit…</div>}
        {truncatedNotice && !streaming && (
          <p className="mt-2 m-0 text-[12px] leading-[1.5] text-[var(--naia-text-secondary)]">
            La réponse a été coupée avant sa fin. Demandez-moi de continuer pour avoir la suite.
          </p>
        )}
        {exhaustedNotice && !streaming && (
          <div className="mt-2 rounded-card bg-naia-info-bg p-[10px_12px] flex flex-col gap-[8px]">
            <p className="m-0 text-[13px] leading-[1.5] text-[var(--naia-text-primary)]">
              {exhaustedNotice}
              <br />
              Je peux reprendre là où je me suis arrêté.
            </p>
            <button
              type="button"
              onClick={onContinue}
              disabled={streaming}
              className="self-start min-h-[44px] min-w-[44px] inline-flex items-center gap-[6px] px-[14px] py-[10px] rounded-[8px] text-[13px] font-semibold text-naia-blue border border-naia-blue bg-transparent cursor-pointer font-[inherit] hover:bg-naia-sky disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ArrowRight size={14} aria-hidden />
              Continuer
            </button>
          </div>
        )}
        {chatError && (
          <div className="mt-2 rounded-card border border-[var(--naia-error,#dc2626)] bg-[color-mix(in_srgb,var(--naia-error,#dc2626)_10%,transparent)] p-[10px_12px] flex flex-col gap-[8px]">
            <div className="flex items-start gap-[8px]">
              <WarningCircle size={16} className="shrink-0 mt-[1px] text-[var(--naia-error,#dc2626)]" weight="fill" aria-hidden />
              <span className="text-[13px] font-semibold text-[var(--naia-error,#dc2626)] leading-[1.4]">{chatError}</span>
            </div>
            {chatErrorIsPartialWrite && (
              <p className="m-0 text-[12px] text-[var(--naia-error,#dc2626)] opacity-80 leading-[1.5]">
                Des modifications ont peut-être été partiellement effectuées. Vérifiez le document avant de réessayer.
              </p>
            )}
            <button
              type="button"
              onClick={() => void onRetry()}
              disabled={streaming}
              className="self-start min-h-[44px] min-w-[44px] inline-flex items-center gap-[6px] px-[14px] py-[10px] rounded-[8px] text-[13px] font-semibold text-[var(--naia-error,#dc2626)] border border-[var(--naia-error,#dc2626)] bg-transparent cursor-pointer font-[inherit] hover:bg-[color-mix(in_srgb,var(--naia-error,#dc2626)_12%,transparent)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ArrowCounterClockwise size={14} aria-hidden />
              Réessayer
            </button>
          </div>
        )}
      </div>
      <form onSubmit={(e) => void send(e)} className="flex flex-col gap-2 p-[8px_12px_calc(8px+env(safe-area-inset-bottom))] border-t border-border">
        {pendingImport && (
          <div className="flex items-center gap-2 self-start rounded-full bg-naia-info-bg px-3 py-1.5 text-[12px] font-semibold text-naia-blue">
            <Paperclip size={13} weight="bold" />
            {isExam
              ? (pendingImport.mime_type === 'application/pdf'
                ? pendingImport.variant === 'answer_key' ? 'Corrigé importé' : 'Sujet importé'
                : 'Photo importée')
              : (pendingImport.mime_type === 'application/pdf' ? 'PDF importé' : 'Photo importée')}
            <button
              type="button"
              onClick={onClearPendingImport}
              aria-label="Retirer la pièce jointe"
              className="flex h-4 w-4 items-center justify-center rounded-full text-naia-blue hover:opacity-70"
            >
              <X size={11} weight="bold" />
            </button>
          </div>
        )}
        {chatDisabled && (
          <p className="m-0 text-[12px] text-[var(--naia-text-secondary)]">{disabledReason}</p>
        )}
        <div className="flex gap-2 items-end">
          <textarea value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={handleKey} disabled={streaming || chatDisabled} rows={2}
            placeholder={chatDisabled ? '' : placeholder}
            className="flex-1 p-[10px_12px] rounded-sm border-[1.5px] border-naia-muted-2 font-[inherit] text-[13px] resize-none bg-background text-foreground outline-none disabled:opacity-50" />
          <Button type="submit" size="icon" disabled={streaming || chatDisabled || (!draft.trim() && !pendingImport)} aria-label="Envoyer" className="size-11 min-h-0 flex-shrink-0 text-[18px]">↑</Button>
        </div>
          <p className="text-[11.5px] text-muted-foreground text-center mt-2">Réponses générées par une IA — vérifiez les informations importantes.</p>
      </form>
    </>
  );
}
